import React from 'react';
import SectionIntro from '../primitives/SectionIntro';
import ApplicantHeaderChip from '../primitives/ApplicantHeaderChip';
import PillGroup from '../primitives/PillGroup';
import DateTriplet from '../primitives/DateTriplet';
import TextField from '../primitives/TextField';
import DependentRepeater from '../primitives/DependentRepeater';
import CategoryCard, { ICONS } from '../primitives/CategoryCard';

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Mx', 'Other'];
const GENDER_OPTIONS = ['Male', 'Female', 'Non-Binary'];
const MARITAL_OPTIONS = ['Single', 'Married', 'Defacto', 'Divorced', 'Separated', 'Widowed'];
const RESIDENT_OPTIONS = ['Citizen', 'Permanent Resident', 'Visa', 'Other'];

class StepGeneralDetails extends React.Component {
  patch = (field, value) => {
    this.props.onUpdateApplicant(this.props.applicantId, { [field]: value });
  };

  render() {
    const { applicants, applicantId, onUpdateApplicant, errors } = this.props;
    const err = errors || {};
    const idx = applicants.findIndex((a) => a.id === applicantId);
    const applicantIndex = idx === -1 ? 0 : idx;
    const applicant = applicants[applicantIndex];
    const isVisa = applicant.resident_status === 'Visa';

    return (
      <div className="space-y-5">
        {/* Intro */}
        <div className="rounded-2xl bg-white shadow-sm border border-[#E5E7EB] p-5 sm:p-8">
          <SectionIntro
            eyebrow="About you - Personal details"
            title="Personal details"
            description="Please ensure the details below match your identity documents exactly (no nicknames or abbreviations), as we'll use them on your loan application."
          />
          <ApplicantHeaderChip
            applicant={applicant}
            applicantIndex={applicantIndex}
            totalApplicants={applicants.length}
          />
        </div>

        {/* Identity */}
        <CategoryCard
          icon={ICONS.identity}
          title="Identity"
          description="Your legal name, title and gender as they appear on ID."
        >
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Title <span className="text-[#DC2626]">*</span>
            </label>
            <PillGroup
              columns={3}
              options={TITLE_OPTIONS}
              value={applicant.title}
              onChange={(v) => this.patch('title', v)}
              fieldId="general.title"
              error={err['general.title']}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Gender <span className="text-[#DC2626]">*</span>
            </label>
            <PillGroup
              columns={3}
              options={GENDER_OPTIONS}
              value={applicant.gender}
              onChange={(v) => this.patch('gender', v)}
              fieldId="general.gender"
              error={err['general.gender']}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label="Given (first) name"
              required
              value={applicant.given_name}
              onChange={(v) => this.patch('given_name', v)}
              autoComplete="given-name"
              fieldId="general.given_name"
              error={err['general.given_name']}
            />
            <TextField
              label="Middle name(s)"
              value={applicant.middle_name}
              onChange={(v) => this.patch('middle_name', v)}
              autoComplete="additional-name"
            />
            <TextField
              label="Family (last) name"
              required
              value={applicant.family_name}
              onChange={(v) => this.patch('family_name', v)}
              autoComplete="family-name"
              fieldId="general.family_name"
              error={err['general.family_name']}
            />
            <TextField
              label="Maiden name (if applicable)"
              value={applicant.maiden_name}
              onChange={(v) => this.patch('maiden_name', v)}
            />
          </div>
        </CategoryCard>

        {/* Contact */}
        <CategoryCard
          icon={ICONS.contact}
          title="Contact"
          description="Where we'll send loan updates and how we can reach you."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label="Email"
              required
              type="email"
              value={applicant.email}
              onChange={(v) => this.patch('email', v)}
              hint="Where we'll send loan updates"
              autoComplete="email"
              fieldId="general.email"
              error={err['general.email']}
            />
            <TextField
              label="Mobile phone"
              required
              inputMode="tel"
              value={applicant.mobile}
              onChange={(v) => this.patch('mobile', v)}
              autoComplete="tel"
              fieldId="general.mobile"
              error={err['general.mobile']}
            />
            <TextField
              label="Home phone"
              inputMode="tel"
              value={applicant.home_phone}
              onChange={(v) => this.patch('home_phone', v)}
            />
            <TextField
              label="Work phone"
              inputMode="tel"
              value={applicant.work_phone}
              onChange={(v) => this.patch('work_phone', v)}
            />
          </div>
        </CategoryCard>

        {/* Date of birth */}
        <CategoryCard
          icon={ICONS.calendar}
          title="Date of birth"
          description="Used to verify your identity."
        >
          <div className="max-w-md">
            <DateTriplet
              label="Date of birth"
              required
              value={applicant.dob}
              onChange={(v) => this.patch('dob', v)}
              fieldId="general.dob"
              error={err['general.dob']}
            />
          </div>
        </CategoryCard>

        {/* Household */}
        <CategoryCard
          icon={ICONS.household}
          title="Household"
          description="Your marital and residency status."
        >
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Marital status <span className="text-[#DC2626]">*</span>
            </label>
            <PillGroup
              columns={3}
              options={MARITAL_OPTIONS}
              value={applicant.martial_status}
              onChange={(v) => this.patch('martial_status', v)}
              fieldId="general.martial_status"
              error={err['general.martial_status']}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Residency status <span className="text-[#DC2626]">*</span>
            </label>
            <PillGroup
              columns={4}
              options={RESIDENT_OPTIONS}
              value={applicant.resident_status}
              onChange={(v) => this.patch('resident_status', v)}
              fieldId="general.resident_status"
              error={err['general.resident_status']}
            />
            {isVisa ? (
              <div className="mt-3">
                <TextField
                  label="What visa do you hold?"
                  required
                  placeholder="e.g. Subclass 482 (TSS), Bridging Visa A, Subclass 189..."
                  value={applicant.resident_visa_details}
                  onChange={(v) => this.patch('resident_visa_details', v)}
                  fieldId="general.resident_visa_details"
                  error={err['general.resident_visa_details']}
                />
              </div>
            ) : null}
          </div>
        </CategoryCard>

        {/* Dependents */}
        <CategoryCard
          icon={ICONS.dependents}
          title="Dependents"
          description="Anyone who financially depends on this applicant. Shared dependents are de-duplicated across applicants."
        >
          <DependentRepeater
            applicants={applicants}
            applicant={applicant}
            onUpdateApplicant={onUpdateApplicant}
            errors={err}
          />
        </CategoryCard>
      </div>
    );
  }
}

export default StepGeneralDetails;
